import React, { useEffect, useState, useMemo } from "react";
import { useGlobal } from "../global/user";
import { Modal, Table, Form, Input, Select, Button, Space, message, Switch, Tag, Spin } from "antd";
import { User, Field, Line } from "../api";
import { PickTGGroupModal } from "./modal/PickTGGroupModal";
import { PickLineSettingModal } from "./modal/PickLineSettingModal";
import { LineConfigAPI, TelegramConfigAPI } from "../constants/GenericAPI";
const { Option } = Select;

export default function FieldTable() {
  const [displayFields, setDisplayFields] = useState([]);
  const [keyword, setKeyword] = useState("");
  const [showNewModeColumns, setShowNewModeColumns] = useState(false);
  const { fields, user, setFields } = useGlobal();
  const [onDeleteField, setOnDeleteField] = useState(false);
  const [target, setTarget] = useState();
  const [onEdit, setOnEdit] = useState(false);

  // 格式化通道顯示的輔助函數
  const formatChannels = (channelData) => {
    if (!channelData) return '無';
    try {
      const data = typeof channelData === 'string' ? JSON.parse(channelData) : channelData;
      const parts = [];
      if (data.LINE && data.LINE.length > 0) {
        const lineNames = data.LINE.map(item => 
          typeof item === 'object' ? `${item.name}` : item
        ).join(', ');
        parts.push(`LINE: ${lineNames}`);
      }
      if (data.TG && data.TG.length > 0) {
        const tgNames = data.TG.map(item => 
          typeof item === 'object' ? `${item.name}` : item
        ).join(', ');
        parts.push(`TG: ${tgNames}`);
      }
      if (parts.length === 0) return '無';
      
      // 使用 React Fragment 和 br 標籤來換行
      return (
        <>
          {parts.map((part, index) => (
            <span 
              key={index} 
              style={{ 
                fontSize: "12px", 
                display: "block",
                backgroundColor: part.startsWith('LINE:') ? 'rgba(24, 144, 255, 0.1)' : 'rgba(250, 173, 20, 0.1)',
                color: part.startsWith('LINE:') ? '#1890ff' : '#faad14',
                border: `1px solid ${part.startsWith('LINE:') ? 'rgba(24, 144, 255, 0.3)' : 'rgba(250, 173, 20, 0.3)'}`,
                padding: '2px 4px',
                borderRadius: '2px',
                marginBottom: '2px'
              }}
            >
              {part}
            </span>
          ))}
        </>
      );
    } catch (e) {
      console.error('JSON 解析失敗:', e, channelData);
      return '解析錯誤';
    }
  };

  // 僅供搜尋用：把通道資料攤平成純文字
  const formatChannelsText = (channelData) => {
    if (!channelData) return '';
    try {
      const data = typeof channelData === 'string' ? JSON.parse(channelData) : channelData;
      const parts = [];
      if (data.LINE?.length > 0) {
        parts.push('LINE: ' + data.LINE.map(i => typeof i === 'object' ? i.name : i).join(', '));
      }
      if (data.TG?.length > 0) {
        parts.push('TG: ' + data.TG.map(i => typeof i === 'object' ? i.name : i).join(', '));
      }
      return parts.join(' ');
    } catch {
      return '';
    }
  };

  useEffect(() => {
    handleSearch();
  }, [keyword, showNewModeColumns, fields]);

  const handleSearch = () => {
    // 先根據模式過濾資料
    let baseData = fields;
    if (showNewModeColumns) {
      // 新模式：只顯示 notification_mode === 1 的場域
      baseData = fields.filter(field => field.notification_mode === 1);
    } else {
      // 舊模式：只顯示 notification_mode !== 1 的場域
      baseData = fields.filter(field => field.notification_mode !== 1);
    }
    
    if (keyword.trim() === "") {
      // 重新轉換所有過濾後的資料
      const transformedFields = baseData.map(field => {
        const transformed = { ...field };
        if (field.notification_mode === 1) {
          transformed.ReportGroup = formatChannels(field.text_notification_channels);
          transformed.WarningGroup = formatChannels(field.image_notification_channels);
          transformed.ServerGroup = formatChannels(field.server_notification_channels);
        } else {
          transformed.ReportGroup = field.ReportGroup || '無';
          transformed.WarningGroup = field.WarningGroup || '無';
          transformed.ServerGroup = field.ServerGroup || '無';
        }
        return transformed;
      });
      setDisplayFields(transformedFields);
      return;
    }

    // 先轉換資料再搜尋
    const transformedData = baseData.map(field => {
      const transformed = { ...field };
      if (field.notification_mode === 1) {
        transformed.ReportGroup = formatChannels(field.text_notification_channels);
        transformed.WarningGroup = formatChannels(field.image_notification_channels);
        transformed.ServerGroup = formatChannels(field.server_notification_channels);
      } else {
        transformed.ReportGroup = field.ReportGroup || '無';
        transformed.WarningGroup = field.WarningGroup || '無';
        transformed.ServerGroup = field.ServerGroup || '無';
      }
      return transformed;
    });

    const filteredData = transformedData.filter((item) => {
      const kw = keyword.toLowerCase();

      const searchTargets = [
        item.field_name,
        item.count?.toString(),
        item.notification_mode?.toString(),
        item.create_time,
        item.name,
      ];

      if (item.notification_mode === 1) {
        // 新模式：通道是物件/JSON，轉純文字後再搜尋
        searchTargets.push(
          formatChannelsText(item.text_notification_channels),
          formatChannelsText(item.image_notification_channels),
          formatChannelsText(item.server_notification_channels),
        );
      } else {
        // 舊模式：ReportGroup 等本來就是字串
        searchTargets.push(item.ReportGroup, item.WarningGroup, item.ServerGroup);
      }

      return searchTargets.some(
        (v) => typeof v === 'string' && v.toLowerCase().includes(kw)
      );
    });
    setDisplayFields(filteredData);
  };

  const columns = [
    { 
      title: "場域名稱", 
      dataIndex: "field_name",
      width: 150,
    },
    { 
      title: <>機具<br/>數目</>, 
      dataIndex: "count", 
      align: "center", 
      width: 60 
    },
    { 
      title: <>通知<br/>模式</>, 
      dataIndex: "notification_mode", 
      align: "center",
      width: 60,
      render: (mode) => (
        <Tag color={mode === 1 ? "green" : "blue"}>
          {mode === 1 ? "新" : "舊"}
        </Tag>
      )
    },
    ...(showNewModeColumns ? [
      { 
        title: <>文字通知<br/>群組</>, 
        dataIndex: "text_notification_channels", 
        align: "center",
        width: 120,
        render: (channels) => formatChannels(channels)
      },
      { 
        title: <>圖片通知<br/>群組</>, 
        dataIndex: "image_notification_channels", 
        align: "center",
        width: 120,
        render: (channels) => formatChannels(channels)
      },
      { 
        title: <>伺服器通知<br/>群組</>, 
        dataIndex: "server_notification_channels", 
        align: "center",
        width: 120,
        render: (channels) => formatChannels(channels)
      },
    ] : [
      { 
        title: "日報群組", 
        dataIndex: "ReportGroup", 
        align: "center",
        width: 120,
        render: (text) => text || '無'
      },
      { 
        title: "警報群組", 
        dataIndex: "WarningGroup", 
        align: "center",
        width: 120,
        render: (text) => text || '無'
      },
      { 
        title: "伺服器群組", 
        dataIndex: "ServerGroup", 
        align: "center", 
        width: 120,
        render: (text) => text || '無'
      },
    ]),
    { 
      title: "主管", 
      dataIndex: "name",
      width: 100,
      align: "center",
    },
    {
      title: "編輯場域",
      width: 120,
      fixed: 'right',
      align: "center",
      render: (field) => (
        <Space>
          <Button
            type="default" style={{ background: "var(--content-background)", color: "var(--primary-text)", border: "2px solid var(--primary-border)" }}
            size="small"
            onClick={() => {
              setTarget(field);
              setOnEdit(true);
            }}
          >
            編輯
          </Button>
          <Button
            danger
            size="small"
            onClick={async () => {
              setTarget(field);
              setOnDeleteField(true);
            }}
          >
            刪除
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <React.Fragment>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
        <Space>
          <Button
            onClick={() => {
              setTarget(null);
              setOnEdit(true);
            }}
            hidden={user.class < process.env.REACT_APP_MANAGER_CALSS_LV}
          >
            新增場域
          </Button>
          <div style={{ display: "flex", alignItems: "center", marginLeft: 16 }}>
            <span style={{ marginRight: 8 }}>模式</span>
            <Switch 
              checked={showNewModeColumns} 
              onChange={setShowNewModeColumns}
              checkedChildren="新"
              unCheckedChildren="舊"
            />
          </div>
        </Space>
        <Space>
          <Input 
            className="search-input"
            placeholder="輸入關鍵字搜尋" 
            value={keyword} 
            onChange={(e) => setKeyword(e.target.value)}
            style={{ width: 200 }} 
          />
        </Space>
      </div>
      <Table
        columns={columns}
        dataSource={displayFields}
        rowKey="field_id"
        pagination={{
          defaultPageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 筆資料`,
          pageSizeOptions: ['10', '20', '50', '100'],
        }}
        bordered
      />
      <FieldEditModel visible={onEdit} setVisible={setOnEdit} field={target} />
      <Modal
        title="確認刪除"
        open={onDeleteField}
        onCancel={() => {
          setOnDeleteField(false);
        }}
        onOk={async () => {
          // console.log("刪除場域 - target 對象:", target);
          // console.log("刪除場域 - target 的所有屬性:", Object.keys(target || {}));
          
          const { field_id } = target;
          
          console.log("刪除場域 - 取得的 field_id:", field_id);
          console.log("刪除場域 - field_id 類型:", typeof field_id);
          
          // 檢查是否 field_id 存在
          if (!field_id) {
            console.error("刪除場域 - field_id 為空!");
            message.error("無法取得場域 ID，刪除失敗");
            return;
          }
          
          const response = await Field.deleteField(field_id);
          
          console.log("刪除場域 - API 回應:", response);
          
          if (response === "successful") {
            setFields((prevFields) => prevFields.filter((field) => field.field_id !== field_id));
            message.success("場域刪除成功");
          } else {
            message.error("場域刪除失敗");
          }
          setOnDeleteField(false);
        }}
        okText="確認刪除"
        cancelText="取消"
        okButtonProps={{ danger: true }}
      >
        <p>確認是否刪除場域「{target?.field_name}」？</p>
        <p style={{ color: '#ff4d4f' }}>此操作無法復原！</p>
      </Modal>
    </React.Fragment>
  );
}

function FieldEditModel({ field, visible, setVisible }) {
  const formItemLayout = {
    labelCol: {
      sm: {
        span: 5,
      },
    },
    wrapperCol: {
      sm: {
        span: 15,
      },
    },
  };
  const [form] = Form.useForm();
  const [managers, setManagers] = useState([]);
  const [LineGroups, setLineGroups] = useState([]);
  const [lineSettings, setLineSettings] = useState([]);
  const [telegramSettings, setTelegramSettings] = useState([]);
  const [useNewMode, setUseNewMode] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // 新增通知通道 state - 存儲完整的對象
  const [selectedTextLINE, setSelectedTextLINE] = useState([]);
  const [selectedTextTG, setSelectedTextTG] = useState([]);
  const [selectedImageLINE, setSelectedImageLINE] = useState([]);
  const [selectedImageTG, setSelectedImageTG] = useState([]);
  const [selectedServerLINE, setSelectedServerLINE] = useState([]);
  const [selectedServerTG, setSelectedServerTG] = useState([]);
  
  // 使用 useMemo 穩定 selectedIds 引用，避免 useEffect 無限循環
  const selectedTextLINEIds = useMemo(() => selectedTextLINE.map(item => typeof item === 'object' ? item.id : item), [selectedTextLINE]);
  const selectedTextTGIds = useMemo(() => selectedTextTG.map(item => typeof item === 'object' ? item.id : item), [selectedTextTG]);
  const selectedImageLINEIds = useMemo(() => selectedImageLINE.map(item => typeof item === 'object' ? item.id : item), [selectedImageLINE]);
  const selectedImageTGIds = useMemo(() => selectedImageTG.map(item => typeof item === 'object' ? item.id : item), [selectedImageTG]);
  const selectedServerLINEIds = useMemo(() => selectedServerLINE.map(item => typeof item === 'object' ? item.id : item), [selectedServerLINE]);
  const selectedServerTGIds = useMemo(() => selectedServerTG.map(item => typeof item === 'object' ? item.id : item), [selectedServerTG]);
  
  // Modal 狀態
  const [showTextModal, setShowTextModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showServerModal, setShowServerModal] = useState(false);
  const [currentModalType, setCurrentModalType] = useState('LINE');
  
  const { setFields } = useGlobal();

  // 輔助函數：格式化通道顯示
  const getChannelNames = (channels) => {
    if (!channels || channels.length === 0) return '未設定';
    
    return channels.map(channel => 
      typeof channel === 'object' ? `${channel.id} - ${channel.name}` : `ID:${channel}`
    ).join(', ');
  };

  useEffect(async () => {
    if (!visible) return;
    
    setLoading(true);
    
    let response = await User.GetManagers();
    if (response.code === 200) setManagers(response.data);
    
    response = await Line.GetLineData();
    if (response.code === 200) setLineGroups(response.data);
    
    // 獲取 LINE 和 Telegram 設定
    try {
      const lineResponse = await LineConfigAPI.getAll();
      if (lineResponse.data?.data) {
        setLineSettings(lineResponse.data.data);
      }
    } catch (error) {
      console.error("獲取 LINE 設定失敗:", error);
    }
    
    try {
      const telegramResponse = await TelegramConfigAPI.getAll();
      if (telegramResponse.data?.data) {
        setTelegramSettings(telegramResponse.data.data);
      }
    } catch (error) {
      console.error("獲取 Telegram 設定失敗:", error);
    }
    
    if (field) {
      const mode = field.notification_mode || 0;
      setUseNewMode(mode === 1);
      
      if (mode === 1) {
        // 解析新模式 JSON
        const parseJSONSafely = (data) => {
          if (!data) return { LINE: [], TG: [] };
          if (typeof data === 'string') {
            try {
              return JSON.parse(data);
            } catch (error) {
              console.error('JSON 解析失敗:', error, data);
              return { LINE: [], TG: [] };
            }
          } else if (typeof data === 'object') {
            return data;
          }
          return { LINE: [], TG: [] };
        };

        const textChannels = parseJSONSafely(field.text_notification_channels);
        const imageChannels = parseJSONSafely(field.image_notification_channels);
        const serverChannels = parseJSONSafely(field.server_notification_channels);
        
        // 直接存儲完整的對象
        setSelectedTextLINE(textChannels.LINE || []);
        setSelectedTextTG(textChannels.TG || []);
        setSelectedImageLINE(imageChannels.LINE || []);
        setSelectedImageTG(imageChannels.TG || []);
        setSelectedServerLINE(serverChannels.LINE || []);
        setSelectedServerTG(serverChannels.TG || []);
      }
      
      form.setFieldsValue(field);
    } else {
      setUseNewMode(false);
      // 清空所有選擇
      setSelectedTextLINE([]);
      setSelectedTextTG([]);
      setSelectedImageLINE([]);
      setSelectedImageTG([]);
      setSelectedServerLINE([]);
      setSelectedServerTG([]);
      form.resetFields();
      console.log("新增模式 - 重置所有狀態");
    }
    
    setLoading(false);
  }, [visible]);

  const onSubmit = async () => {
    try {
      const values = form.getFieldsValue();
      const { field_name, manager_id } = values;
      const notification_mode = useNewMode ? 1 : 0;
      
      console.log("onSubmit values:", values);
      console.log("useNewMode:", useNewMode);
      console.log("notification_mode:", notification_mode);
    
    if (useNewMode) {
      const textChannels = JSON.stringify({ 
        LINE: selectedTextLINE.map(item => typeof item === 'object' ? item.id : item), 
        TG: selectedTextTG.map(item => typeof item === 'object' ? item.id : item) 
      });
      const imageChannels = JSON.stringify({ 
        LINE: selectedImageLINE.map(item => typeof item === 'object' ? item.id : item), 
        TG: selectedImageTG.map(item => typeof item === 'object' ? item.id : item) 
      });
      const serverChannels = JSON.stringify({ 
        LINE: selectedServerLINE.map(item => typeof item === 'object' ? item.id : item), 
        TG: selectedServerTG.map(item => typeof item === 'object' ? item.id : item) 
      });
      
      console.log("New mode channels:", {
        textChannels,
        imageChannels,
        serverChannels,
        selectedTextLINE,
        selectedTextTG,
        selectedImageLINE,
        selectedImageTG,
        selectedServerLINE,
        selectedServerTG
      });
      
      // 檢查是否所有必填字段都有值
      if (!field_name || !manager_id) {
        message.error("請填寫場域名稱和選擇主管");
        return;
      }
      
      if (field) {
        // console.log("更新場域 - 新模式:");
        // console.log("- field 對象:", field);
        // console.log("- field.id:", field.id, "(類型:", typeof field.id, ")");
        // console.log("- field.field_id:", field.field_id, "(類型:", typeof field.field_id, ")");
        // console.log("- field_name:", field_name);
        // console.log("- manager_id:", manager_id);
        // console.log("- notification_mode:", notification_mode);
        // console.log("- textChannels:", textChannels);
        // console.log("- imageChannels:", imageChannels);
        // console.log("- serverChannels:", serverChannels);
        
        const fieldId = field.field_id || field.id; // 優先使用 field_id
        console.log("- 使用的 fieldId:", fieldId);
        
        const response = await Field.UpdateField(
          fieldId, 
          field_name, 
          manager_id, 
          null, 
          null, 
          null, 
          notification_mode, 
          textChannels, 
          imageChannels, 
          serverChannels
        );
        
        console.log("更新場域回應 - 新模式:", response);
        
        if (response.code === 200) {
          // console.log("更新成功，更新 state - 新模式");
          // console.log("- response.data:", response.data);
          // console.log("- 比較用的 fieldId:", fieldId);
          
          setFields((data) => {
            // console.log("- 更新前的 data:", data);
            const updatedData = [...data].map((f) => {
              // console.log(`  檢查項目: f.field_id=${f.field_id}, f.id=${f.id}, 目標fieldId=${fieldId}`);
              // 優先使用 field_id 比較，如果沒有則使用 id
              const currentFieldId = f.field_id;
              if (currentFieldId === fieldId) {
                // console.log("    匹配！更新此項目");
                return { ...f, ...response.data }; // 保留原有資料並合併更新
              }
              return f;
            });
            // console.log("- 更新後的 data:", updatedData);
            return updatedData;
          });
          message.success("更新成功");
        } else {
          message.error(response.message || "更新失敗");
        }
      } else {
        console.log("創建新場域，參數:", {
          field_name,
          manager_id,
          report_line_id: null,
          warning_line_id: null,
          server_line_id: null,
          notification_mode,
          textChannels,
          imageChannels,
          serverChannels
        });
        
        const response = await Field.CreateField(
          field_name, 
          manager_id, 
          null, 
          null, 
          null, 
          notification_mode, 
          textChannels, 
          imageChannels, 
          serverChannels
        );
        
        console.log("創建場域回應:", response);
        
        if (response.code === 200) {
          setFields((data) => [...data, response.data]);
          message.success("新增成功");
        } else {
          message.error(response.message || "新增失敗");
        }
      }
    } else {
      const { report_line_id, warning_line_id, server_line_id } = values;
      
      if (field) {
        // console.log("更新場域 - 舊模式:");
        // console.log("- field 對象:", field);
        // console.log("- field.id:", field.id, "(類型:", typeof field.id, ")");
        // console.log("- field.field_id:", field.field_id, "(類型:", typeof field.field_id, ")");
        // console.log("- field_name:", field_name);
        // console.log("- manager_id:", manager_id);
        // console.log("- report_line_id:", report_line_id);
        // console.log("- warning_line_id:", warning_line_id);
        // console.log("- server_line_id:", server_line_id);
        // console.log("- notification_mode:", notification_mode);
        
        const fieldId = field.field_id;
        // console.log("- 使用的 fieldId:", fieldId);
        
        const response = await Field.UpdateField(
          fieldId, 
          field_name, 
          manager_id, 
          report_line_id, 
          warning_line_id, 
          server_line_id, 
          notification_mode, 
          null, 
          null, 
          null
        );
        
        // console.log("更新場域回應 - 舊模式:", response);
        
        if (response.code === 200) {
          // console.log("更新成功，更新 state - 舊模式");
          // console.log("- response.data:", response.data);
          // console.log("- 比較用的 fieldId:", fieldId);
          
          setFields((data) => {
            // console.log("- 更新前的 data:", data);
            const updatedData = [...data].map((f) => {
              // console.log(`  檢查項目: f.field_id=${f.field_id}, f.id=${f.id}, 目標fieldId=${fieldId}`);
              // 優先使用 field_id 比較，如果沒有則使用 id
              const currentFieldId = f.field_id || f.id;
              if (currentFieldId === fieldId) {
                // console.log("    匹配！更新此項目");
                return { ...f, ...response.data }; // 保留原有資料並合併更新
              }
              return f;
            });
            // console.log("- 更新後的 data:", updatedData);
            return updatedData;
          });
          message.success("更新成功");
        } else {
          message.error(response.message || "更新失敗");
        }
      } else {
        const response = await Field.CreateField(
          field_name, 
          manager_id, 
          report_line_id, 
          warning_line_id, 
          server_line_id, 
          notification_mode, 
          null, 
          null, 
          null
        );
        if (response.code === 200) {
          setFields((data) => [...data, response.data]);
          message.success("新增成功");
        } else {
          message.error(response.message || "新增失敗");
        }
      }
    }
    setVisible(false);
  } catch (error) {
    console.error("onSubmit 錯誤:", error);
    message.error("操作失敗: " + (error.message || "未知錯誤"));
  }
  };

  return (
    <React.Fragment>
      <Modal
        open={visible}
        title={field ? "編輯場域" : "新增場域"}
        onCancel={() => setVisible(false)}
        onOk={() => form.submit()}
        okText="完成"
        cancelText="取消"
        width={700}
        confirmLoading={loading}
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>載入中...</div>
          </div>
        ) : (
          <Form form={form} onFinish={onSubmit} {...formItemLayout}>
          <Form.Item 
            label="場域名稱" 
            name="field_name" 
            rules={[{ required: true, message: "請輸入場域名稱" }]}
          >
            <Input placeholder="請輸入場域名稱" />
          </Form.Item>
          
          <Form.Item 
            label="主管" 
            name="manager_id" 
            rules={[{ required: true, message: "請選擇單位主管" }]}
          >
            <Select placeholder="請選擇單位主管">
              {managers.map(({ value, text }) => (
                <Option value={value} key={value}>
                  {text}
                </Option>
              ))}
            </Select>
          </Form.Item>
          
          <Form.Item label="通知模式">
            <Space>
              <Switch 
                checked={useNewMode} 
                onChange={setUseNewMode}
                checkedChildren="新模式"
                unCheckedChildren="舊模式"
              />
              <span style={{ color: '#999', fontSize: 12 }}>
                {useNewMode ? '(支援多通道 LINE/TG)' : '(單一群組設定)'}
              </span>
            </Space>
          </Form.Item>
          
          {useNewMode ? (
            <>
              <Form.Item label="文字通知通道">
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Space>
                    <Button onClick={() => { setCurrentModalType('LINE'); setShowTextModal(true); }}>
                      選擇 LINE 群組
                    </Button>
                    <Button onClick={() => { setCurrentModalType('TG'); setShowTextModal(true); }}>
                      選擇 TG 群組
                    </Button>
                  </Space>
                  <div style={{ padding: '8px', background: 'var(--content-background)', border: '1px solid var(--primary-border)', borderRadius: 4 }}>
                    <div><strong>LINE:</strong> {getChannelNames(selectedTextLINE, 'LINE')}</div>
                    <div><strong>TG:</strong> {getChannelNames(selectedTextTG, 'TG')}</div>
                  </div>
                </Space>
              </Form.Item>
              
              <Form.Item label="圖片通知通道">
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Space>
                    <Button onClick={() => { setCurrentModalType('LINE'); setShowImageModal(true); }}>
                      選擇 LINE 群組
                    </Button>
                    <Button onClick={() => { setCurrentModalType('TG'); setShowImageModal(true); }}>
                      選擇 TG 群組
                    </Button>
                  </Space>
                  <div style={{ padding: '8px', background: 'var(--content-background)', border: '1px solid var(--primary-border)', borderRadius: 4 }}>
                    <div><strong>LINE:</strong> {getChannelNames(selectedImageLINE, 'LINE')}</div>
                    <div><strong>TG:</strong> {getChannelNames(selectedImageTG, 'TG')}</div>
                  </div>
                </Space>
              </Form.Item>
              
              <Form.Item label="伺服器通知通道">
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Space>
                    <Button onClick={() => { setCurrentModalType('LINE'); setShowServerModal(true); }}>
                      選擇 LINE 群組
                    </Button>
                    <Button onClick={() => { setCurrentModalType('TG'); setShowServerModal(true); }}>
                      選擇 TG 群組
                    </Button>
                  </Space>
                  <div style={{ padding: '8px', background: 'var(--content-background)', border: '1px solid var(--primary-border)', borderRadius: 4 }}>
                    <div><strong>LINE:</strong> {getChannelNames(selectedServerLINE, 'LINE')}</div>
                    <div><strong>TG:</strong> {getChannelNames(selectedServerTG, 'TG')}</div>
                  </div>
                </Space>
              </Form.Item>
            </>
          ) : (
            <>
              <Form.Item 
                label="日報群組" 
                name="report_line_id" 
                rules={[{ required: true, message: "請選擇日報群組" }]}
              >
                <Select placeholder="請選擇日報群組">
                  {LineGroups.map(({ id, name }) => (
                    <Option value={id} key={id}>
                      {name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              
              <Form.Item 
                label="警報群組" 
                name="warning_line_id" 
                rules={[{ required: true, message: "請選擇警報群組" }]}
              >
                <Select placeholder="請選擇警報群組">
                  {LineGroups.map(({ id, name }) => (
                    <Option value={id} key={id}>
                      {name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              
              <Form.Item 
                label="伺服器群組" 
                name="server_line_id" 
                rules={[{ required: true, message: "請選擇伺服器群組" }]}
              >
                <Select placeholder="請選擇伺服器群組">
                  {LineGroups.map(({ id, name }) => (
                    <Option value={id} key={id}>
                      {name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </>
          )}
        </Form>
        )}
      </Modal>
      
      {/* 文字通知 LINE Modal */}
      <PickLineSettingModal
        visible={showTextModal && currentModalType === 'LINE'}
        onCancel={() => setShowTextModal(false)}
        onPickup={(selectedIds) => {
          if (selectedIds === null) {
            // 清除選擇
            setSelectedTextLINE([]);
          } else {
            // 將 ID 轉換為包含 id 和 name 的對象，使用 LINE 設定的 setting_name
            const selectedChannels = selectedIds.map(id => {
              const setting = lineSettings.find(s => s.id === id);
              return setting ? { id, name: setting.setting_name } : { id, name: `ID:${id}` };
            });
            setSelectedTextLINE(selectedChannels);
          }
          setShowTextModal(false);
        }}
        selectedGroup={{ group_name: "文字通知通道" }}
        selectedLineSetting={null}
        selectedIds={selectedTextLINEIds}
        multiSelect={false}
      />
      
      {/* 文字通知 TG Modal */}
      <PickTGGroupModal
        visible={showTextModal && currentModalType === 'TG'}
        onCancel={() => setShowTextModal(false)}
        onPickup={(selectedIds) => {
          if (selectedIds === null) {
            // 清除選擇
            setSelectedTextTG([]);
          } else {
            // 將 ID 轉換為包含 id 和 name 的對象，使用 Telegram 設定的 setting_name
            const selectedChannels = selectedIds.map(id => {
              const setting = telegramSettings.find(s => s.id === id);
              return setting ? { id, name: setting.setting_name } : { id, name: `ID:${id}` };
            });
            setSelectedTextTG(selectedChannels);
          }
          setShowTextModal(false);
        }}
        selectedGroup={{ group_name: "文字通知通道" }}
        selectedTGSetting={null}
        selectedIds={selectedTextTGIds}
        multiSelect={false}
      />
      
      {/* 圖片通知 LINE Modal */}
      <PickLineSettingModal
        visible={showImageModal && currentModalType === 'LINE'}
        onCancel={() => setShowImageModal(false)}
        onPickup={(selectedIds) => {
          if (selectedIds === null) {
            // 清除選擇
            setSelectedImageLINE([]);
          } else {
            // 將 ID 轉換為包含 id 和 name 的對象，使用 LINE 設定的 setting_name
            const selectedChannels = selectedIds.map(id => {
              const setting = lineSettings.find(s => s.id === id);
              return setting ? { id, name: setting.setting_name } : { id, name: `ID:${id}` };
            });
            setSelectedImageLINE(selectedChannels);
          }
          setShowImageModal(false);
        }}
        selectedGroup={{ group_name: "圖片通知通道" }}
        selectedLineSetting={null}
        selectedIds={selectedImageLINEIds}
        multiSelect={false}
      />
      
      {/* 圖片通知 TG Modal */}
      <PickTGGroupModal
        visible={showImageModal && currentModalType === 'TG'}
        onCancel={() => setShowImageModal(false)}
        onPickup={(selectedIds) => {
          if (selectedIds === null) {
            // 清除選擇
            setSelectedImageTG([]);
          } else {
            // 將 ID 轉換為包含 id 和 name 的對象，使用 Telegram 設定的 setting_name
            const selectedChannels = selectedIds.map(id => {
              const setting = telegramSettings.find(s => s.id === id);
              return setting ? { id, name: setting.setting_name } : { id, name: `ID:${id}` };
            });
            setSelectedImageTG(selectedChannels);
          }
          setShowImageModal(false);
        }}
        selectedGroup={{ group_name: "圖片通知通道" }}
        selectedTGSetting={null}
        selectedIds={selectedImageTGIds}
        multiSelect={false}
      />
      
      {/* 伺服器通知 LINE Modal */}
      <PickLineSettingModal
        visible={showServerModal && currentModalType === 'LINE'}
        onCancel={() => setShowServerModal(false)}
        onPickup={(selectedIds) => {
          if (selectedIds === null) {
            // 清除選擇
            setSelectedServerLINE([]);
          } else {
            // 將 ID 轉換為包含 id 和 name 的對象，使用 LINE 設定的 setting_name
            const selectedChannels = selectedIds.map(id => {
              const setting = lineSettings.find(s => s.id === id);
              return setting ? { id, name: setting.setting_name } : { id, name: `ID:${id}` };
            });
            setSelectedServerLINE(selectedChannels);
          }
          setShowServerModal(false);
        }}
        selectedGroup={{ group_name: "伺服器通知通道" }}
        selectedLineSetting={null}
        selectedIds={selectedServerLINEIds}
        multiSelect={false}
      />
      
      {/* 伺服器通知 TG Modal */}
      <PickTGGroupModal
        visible={showServerModal && currentModalType === 'TG'}
        onCancel={() => setShowServerModal(false)}
        onPickup={(selectedIds) => {
          if (selectedIds === null) {
            // 清除選擇
            setSelectedServerTG([]);
          } else {
            // 將 ID 轉換為包含 id 和 name 的對象，使用 Telegram 設定的 setting_name
            const selectedChannels = selectedIds.map(id => {
              const setting = telegramSettings.find(s => s.id === id);
              return setting ? { id, name: setting.setting_name } : { id, name: `ID:${id}` };
            });
            setSelectedServerTG(selectedChannels);
          }
          setShowServerModal(false);
        }}
        selectedGroup={{ group_name: "伺服器通知通道" }}
        selectedTGSetting={null}
        selectedIds={selectedServerTGIds}
        multiSelect={false}
      />
    </React.Fragment>
  );
}